/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

const APP_NAME = 'Prakash Astrologer';
const DEFAULT_DESCRIPTION = 'Get personalised horoscope readings, astrological consultations, and gemstone recommendations from Prakash Astrologer.';
const DEFAULT_IMAGE = 'https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg';
const BASE_URL = process.env.REACT_APP_URL || 'https://astrology.prakashastrologer.com';

const SEO = ({
    title,
    description,
    image,
    url,
    type,
    article,
    noindex,
    jsonLd,
}) => {
    const pageTitle = title ? `${title} | ${APP_NAME}` : APP_NAME;
    const pageDescription = description || DEFAULT_DESCRIPTION;
    const pageImage = image || DEFAULT_IMAGE;
    const pageUrl = url ? `${BASE_URL}${url}` : BASE_URL;
    const pageType = type || 'website';

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Canonical URL */}
            <link rel="canonical" href={pageUrl} />

            {/* Open Graph Tags */}
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            <meta property="og:image" content={pageImage} />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:type" content={pageType} />
            <meta property="og:site_name" content={APP_NAME} />

            {/* Article specific OG tags */}
            {article && article.publishedTime && (
                <meta property="article:published_time" content={article.publishedTime} />
            )}
            {article && article.modifiedTime && (
                <meta property="article:modified_time" content={article.modifiedTime} />
            )}
            {article && article.author && (
                <meta property="article:author" content={article.author} />
            )}

            {/* Twitter Card Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            <meta name="twitter:image" content={pageImage} />

            {/* JSON-LD Structured Data */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
};

SEO.defaultProps = {
    title: null,
    description: null,
    image: null,
    url: null,
    type: 'website',
    article: null,
    noindex: false,
    jsonLd: null,
};

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    url: PropTypes.string,
    type: PropTypes.string,
    article: PropTypes.shape({
        publishedTime: PropTypes.string,
        modifiedTime: PropTypes.string,
        author: PropTypes.string,
    }),
    noindex: PropTypes.bool,
    jsonLd: PropTypes.object,
};

export default SEO;
